/* ==========================================================================
   BM GLOBAL WIPES — Animation Framework
   Vanilla JS, no dependencies. Respects prefers-reduced-motion.
   ========================================================================== */

(() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    document.documentElement.classList.add('bm-reduced-motion');
    return;
  }

  /* ------------------------------------------------------------------------
     1. SCROLL REVEAL
     Any element with [data-bm-reveal] gets .bm-is-visible when it enters view.
     Parent [data-bm-stagger] auto-numbers children via --bm-stagger-index.
     ------------------------------------------------------------------------ */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('bm-is-visible');
          revealObserver.unobserve(entry.target);
        }
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
  );

  const initReveal = (root = document) => {
    root.querySelectorAll('[data-bm-stagger]').forEach((container) => {
      const children = container.querySelectorAll('[data-bm-reveal]');
      children.forEach((child, i) => {
        child.style.setProperty('--bm-stagger-index', i);
      });
    });
    root.querySelectorAll('[data-bm-reveal]').forEach((el) => revealObserver.observe(el));
  };

  /* ------------------------------------------------------------------------
     2. SPLIT-TEXT
     Any element with .bm-split-text has its text split into word spans.
     Combines with reveal: element gets .bm-is-visible via IntersectionObserver.
     ------------------------------------------------------------------------ */
  const splitObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('bm-is-visible');
          splitObserver.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.2 }
  );

  const initSplit = (root = document) => {
    root.querySelectorAll('.bm-split-text:not([data-bm-split-done])').forEach((el) => {
      const words = el.textContent.trim().split(/\s+/);
      el.innerHTML = words
        .map((w, i) => `<span class="bm-word" style="--bm-word-index:${i}">${w}</span>`)
        .join(' ');
      el.setAttribute('data-bm-split-done', '');
      splitObserver.observe(el);
    });
  };

  /* ------------------------------------------------------------------------
     3. NUMBER COUNTER
     <span class="bm-count" data-bm-target="10000" data-bm-suffix="+">0</span>
     Counts up when first visible.
     ------------------------------------------------------------------------ */
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const runCounter = (el) => {
    const target = parseFloat(el.dataset.bmTarget || '0');
    const duration = parseInt(el.dataset.bmDuration || '1600', 10);
    const suffix = el.dataset.bmSuffix || '';
    const prefix = el.dataset.bmPrefix || '';
    const decimals = parseInt(el.dataset.bmDecimals || '0', 10);
    const locale = document.documentElement.lang || 'de-DE';
    const formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    const start = performance.now();

    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const value = target * easeOutCubic(t);
      el.textContent = `${prefix}${formatter.format(value)}${suffix}`;
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const counterObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          runCounter(entry.target);
          counterObserver.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.4 }
  );

  const initCounters = (root = document) => {
    root.querySelectorAll('.bm-count:not([data-bm-count-done])').forEach((el) => {
      el.setAttribute('data-bm-count-done', '');
      counterObserver.observe(el);
    });
  };

  /* ------------------------------------------------------------------------
     4. PARALLAX
     <div data-bm-parallax="0.2">...</div>  — strength 0..1
     Only runs on pointer-fine devices to save mobile battery.
     ------------------------------------------------------------------------ */
  const parallaxTargets = [];
  let rafPending = false;

  const updateParallax = () => {
    rafPending = false;
    const vh = window.innerHeight;
    for (const { el, strength } of parallaxTargets) {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) continue;
      const center = rect.top + rect.height / 2;
      const offset = (center - vh / 2) * -strength;
      el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
    }
  };

  const requestParallax = () => {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(updateParallax);
  };

  const initParallax = (root = document) => {
    if (!window.matchMedia('(pointer: fine)').matches) return;
    root.querySelectorAll('[data-bm-parallax]:not([data-bm-parallax-done])').forEach((el) => {
      const strength = parseFloat(el.getAttribute('data-bm-parallax')) || 0.15;
      el.setAttribute('data-bm-parallax-done', '');
      parallaxTargets.push({ el, strength: Math.max(0, Math.min(0.5, strength)) });
    });
    if (parallaxTargets.length && !window.__bmParallaxBound) {
      window.__bmParallaxBound = true;
      window.addEventListener('scroll', requestParallax, { passive: true });
      window.addEventListener('resize', requestParallax);
      requestParallax();
    }
  };

  /* ------------------------------------------------------------------------
     5. HEADER SCROLL STATE
     Toggles .is-scrolled on #header-component after 24px scroll.
     ------------------------------------------------------------------------ */
  const initHeaderScroll = () => {
    const header = document.querySelector('#header-component');
    if (!header) return;
    let scrolled = false;
    const update = () => {
      const shouldBe = window.scrollY > 24;
      if (shouldBe !== scrolled) {
        scrolled = shouldBe;
        header.classList.toggle('is-scrolled', scrolled);
      }
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  };

  /* ------------------------------------------------------------------------
     6. INIT + RE-INIT (for Shopify section reloads in editor / dynamic morphs)
     ------------------------------------------------------------------------ */
  const initAll = (root = document) => {
    initReveal(root);
    initSplit(root);
    initCounters(root);
    initParallax(root);
  };

  // Header scroll state only runs once (global, no per-section re-init)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeaderScroll);
  } else {
    initHeaderScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Re-init on Shopify section rerender (theme editor & AJAX section swaps)
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:reorder', () => initAll());

  // Expose for section-hydration hooks if we ever need to re-run manually
  window.BMAnimations = { init: initAll };
})();
